import React, {
  useMemo,
  FC,
}                       from 'react';
import styled           from 'styled-components';
import * as Yup         from 'yup';
import {
  Field,
  Form,
  Formik,
  FormikProps,
}                       from 'formik';
import {
  Container,
  Dialog,
  Grid,
}                       from '@material-ui/core';
import {
  Button,
  TextInput,
}                       from '@components/index';
import { phoneRegex }   from '@utils/regexpPaterns';
import { ISalesPerson } from '@typings/accountManagement';
import { createStyles, withStyles } from '@material-ui/styles';

const ModalFormWrapper = styled.div`
  padding    : 40px 5%;
  text-align : ${({ theme: { width } }): string | false => ['xs', 'sm'].includes(width) && 'center'};

  h1 {
    display: inline-block;
  }
`;

const FieldWrapper = styled.div`
  margin-inline : ${({ theme: { width } }): string | false => ['xs', 'sm'].includes(width) && 'auto'};
  display       : flex;
  flex-wrap     : wrap;
  width         : 300px;
  margin-top    : 10px;

  p {
    width      : 100%;
    font-size  : 16px;
    text-align : left;
  }

  div {
    text-align : left;
  }

  .MuiInputBase-root{
    max-width : 320px;
    min-width : 260px;
  }
`;

const StyledButtonsWrapper = withStyles(({ breakpoints, spacing }) => createStyles({
  root: {
    display        : 'flex',
    justifyContent : 'flex-end',
    marginBottom   : spacing(1.25),
    marginTop      : spacing(3),

    [breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
}))(Container);

const AchField = styled(Field)`
  input::placeholder {
    opacity   : 1;
    font-size : 28px;
    color     : #0063A5 !important;
  }

  input[value=""] {
    padding: 15.5px 14px 5px;
  }
`;

const ACHBillingHeader = styled.p`
   font-weight : 500;
   margin-top  : 25px;
`;

const Spacer = styled.div`
   margin: 50px;
`;

const validationSchema = Yup.object().shape({
  mailingAddress : Yup.string().nullable().notRequired(),
  email          : Yup.string().email().nullable().notRequired(),
  firstName      : Yup.string().nullable(),
  lastName       : Yup.string().nullable()
    .when('firstName', {
      is        : (firstName) => !!firstName,
      then      : Yup.string().required(),
      otherwise : Yup.string().notRequired(),
    }),
  organization: Yup.string().nullable()
    .when(['firstName', 'lastName'], {
      is        : (firstName, lastName) => !!firstName && !!lastName,
      then      : Yup.string().notRequired(),
      otherwise : Yup.string().required(),
    }),
  phone: Yup.string().nullable().min(10)
    .max(10)
    .matches(phoneRegex)
    .notRequired(),
  achInformation: Yup.object().shape({
    bankName                   : Yup.string().nullable().notRequired(),
    routingNumber              : Yup.string().nullable().notRequired(),
    accountNumber              : Yup.string().nullable().notRequired(),
    encodedAuthorizationNumber : Yup.string().nullable().notRequired(),
  }),
});

const getInitialValues = (data?: ISalesPerson | null): ISalesPerson => ({
  id             : data?.id ?? '',
  organization   : data?.organization ?? '',
  firstName      : data?.firstName ?? '',
  lastName       : data?.lastName ?? '',
  phone          : data?.phone ?? '',
  mailingAddress : data?.mailingAddress ?? '',
  email          : data?.email ?? '',
  achInformation : {
    bankName                   : data?.achInformation?.bankName ?? '',
    routingNumber              : data?.achInformation?.routingNumber ?? '',
    accountNumber              : data?.achInformation?.accountNumber ?? '',
    encodedAuthorizationNumber : data?.achInformation?.encodedAuthorizationNumber ?? '',
  },
});

interface IProps {
  isOpen                : boolean;
  onCancel              : () => void;
  handleSaveSalesperson : (data: ISalesPerson) => Promise<void>;
  salesPerson           : ISalesPerson | null;
  isUpdating            : boolean;
}

export const AddSalesPersonModal: FC<IProps> = ({
  isOpen,
  onCancel,
  handleSaveSalesperson,
  salesPerson,
  isUpdating,
}) => {
  const initialValues = useMemo<ISalesPerson>(
    () => getInitialValues(salesPerson),
    [salesPerson],
  );

  return (
    <Dialog
      open            = {isOpen}
      onClose         = {onCancel}
      onEscapeKeyDown = {onCancel}
      PaperProps      = {{
        style: {
          borderRadius        : 15,
          borderTopLeftRadius : 0,
          padding             : '40px',
          maxWidth            : '870px',
          width               : '100%',
        },
      }}
    >
      <Formik
        initialValues    = {initialValues}
        onSubmit         = {handleSaveSalesperson}
        validationSchema = {validationSchema}
      >
        {({
          dirty,
          errors,
          touched,
          isValid,
          isSubmitting,
          values,
        }: FormikProps<ISalesPerson>): JSX.Element => (
          <Form>
            <ModalFormWrapper>
              <Grid container justify="space-between" spacing={2}>
                <Grid item xs={12}>
                  <h1>Add Salesperson Information</h1>
                </Grid>

                <Grid item xs={12} sm={6} lg={5}>
                  <FieldWrapper>
                    <p>First name</p>
                    <Field
                      id          = "firstName"
                      name        = "firstName"
                      type        = "tel"
                      placeholder = ""
                      inputProps  = {{ maxLength: 55 }}
                      component   = {TextInput}
                      error       = {!!errors?.firstName}
                    />
                  </FieldWrapper>
                </Grid>

                <Grid item xs={12} sm={6} lg={5}>
                  <FieldWrapper>
                    <p>Last Name</p>
                    <Field
                      id          = "lastName"
                      name        = "lastName"
                      type        = "tel"
                      placeholder = ""
                      inputProps  = {{ maxLength: 55 }}
                      component   = {TextInput}
                      error       = {!!errors?.lastName}
                    />
                  </FieldWrapper>
                </Grid>
              </Grid>

              <Grid container justify="space-between" spacing={2}>
                <Grid item xs={12} sm={6} lg={5}>
                  <FieldWrapper>
                    <p>Organization Name</p>
                    <Field
                      id          = "organization"
                      name        = "organization"
                      placeholder = ""
                      inputProps  = {{ maxLength: 55 }}
                      component   = {TextInput}
                      error       = {!!errors?.organization}
                    />
                  </FieldWrapper>
                </Grid>

                <Grid item xs={12} sm={6} lg={5}>
                  <FieldWrapper>
                    <p>Phone</p>
                    <Field
                      id          = "phone"
                      name        = "phone"
                      type        = "tel"
                      error       = {touched.phone && !!errors.phone}
                      placeholder = ""
                      component   = {TextInput}
                    />
                  </FieldWrapper>
                </Grid>
              </Grid>

              <Grid container justify="space-between" spacing={2}>
                <Grid item xs={12} sm={6} lg={5}>
                  <FieldWrapper>
                    <p>Mailing Address</p>
                    <Field
                      id         = "mailingAddress"
                      name       = "mailingAddress"
                      type       = "text"
                      inputProps = {{ maxLength: 75 }}
                      component  = {TextInput}
                    />
                  </FieldWrapper>
                </Grid>

                <Grid item xs={12} sm={6} lg={5}>
                  <FieldWrapper>
                    <p>Email </p>
                    <Field
                      id         = "email"
                      name       = "email"
                      type       = "email"
                      inputProps = {{ maxLength: 150 }}
                      error      = {touched.email && !!errors.email}
                      component  = {TextInput}
                      stripChars = "[^a-zA-Z0-9@._+\-\s]*"
                    />
                  </FieldWrapper>
                </Grid>
              </Grid>

              <Grid container justify="space-between" spacing={2}>
                <Grid item xs={12} sm={6} lg={5}>
                  <ACHBillingHeader>ACH Billing Information</ACHBillingHeader>
                </Grid>
              </Grid>

              <Grid container justify="space-between" spacing={2}>
                <Grid item xs={12} sm={6} lg={5}>
                  <FieldWrapper>
                    <p>Bank name</p>
                    <Field
                      id         = "achInformation.bankName"
                      name       = "achInformation.bankName"
                      type       = "text"
                      inputProps = {{ maxLength: 55 }}
                      component  = {TextInput}
                    />
                  </FieldWrapper>
                </Grid>

                <Grid item xs={12} sm={6} lg={5}>
                  <FieldWrapper>
                    <p>Routing Number</p>
                    <Field
                      id         = "achInformation.routingNumber"
                      name       = "achInformation.routingNumber"
                      type       = "text"
                      inputProps = {{ maxLength: 25 }}
                      component  = {TextInput}
                    />
                  </FieldWrapper>
                </Grid>
              </Grid>

              <Grid container justify="space-between" spacing={2}>
                <Grid item xs={12} sm={6} lg={5}>
                  <FieldWrapper>
                    <p>Account Number</p>
                    <Field
                      id         = "achInformation.accountNumber"
                      name       = "achInformation.accountNumber"
                      type       = "text"
                      inputProps = {{ maxLength: 25 }}
                      component  = {TextInput}
                    />
                  </FieldWrapper>
                </Grid>

                <Grid item xs={12} sm={6} lg={5}>
                  <FieldWrapper>
                    <p>Encoded Authorization Number</p>
                    <AchField
                      id          = "achInformation.encodedAuthorizationNumber"
                      name        = "achInformation.encodedAuthorizationNumber"
                      type        = "password"
                      placeholder = {values?.achInformation?.encodedAuthorizationNumber ? '•••••••••' : ''}
                      component   = {TextInput}
                    />
                  </FieldWrapper>
                </Grid>
              </Grid>

              <Spacer />

              <Grid container justify="flex-end">
                <StyledButtonsWrapper>
                  <Button
                    variant = "outlined"
                    label   = "Cancel"
                    margin  = "5px"
                    onClick = {onCancel}
                  />
                  <Button
                    type      = "submit"
                    label     = {values.id ? 'Edit Salesperson' : 'Add Salesperson'}
                    isLoading = {isUpdating || isSubmitting}
                    margin    = "5px"
                    width     = "200px"
                    disabled  = {!isValid || !dirty || isUpdating}
                  />
                </StyledButtonsWrapper>
              </Grid>
            </ModalFormWrapper>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
